import { pending, type PendingSubmission } from "./offlineStore";
import { replayOfflineSession, submitPractice } from "../api/practice";
import { ApiError, currentUserId } from "../api/client";

/**
 * Drains the queue of answers given while offline.
 *
 * Runs when the browser says it is back online, and once at startup — a student
 * may have closed the tab while offline and reopened it with a connection, in
 * which case no `online` event ever fires.
 *
 * A submission is removed only once the server has taken it. A network failure
 * leaves it queued for the next attempt; a 4xx removes it, because a request
 * the server refuses will be refused identically forever and retrying it every
 * reconnect is a queue that never empties.
 */
export async function drainPending(): Promise<{ sent: number; kept: number }> {
  let sent = 0;
  let kept = 0;

  let queued: PendingSubmission[];
  try {
    queued = await pending.all();
  } catch {
    // Storage unavailable — nothing queued, nothing to do.
    return { sent: 0, kept: 0 };
  }

  // Only the signed-in student's own answers go up. Another student's queue on
  // a shared phone waits for them; an untagged legacy item has no owner we can
  // prove, so it is dropped rather than credited to whoever is signed in now.
  const me = currentUserId();
  if (!me) return { sent: 0, kept: queued.length };

  for (const submission of queued) {
    if (!submission.ownerId) {
      await pending.remove(submission.id);
      continue;
    }
    if (submission.ownerId !== me) {
      kept += 1;
      continue;
    }

    try {
      if (submission.sessionId) {
        await submitPractice(submission.sessionId, submission.answers, submission.id);
      } else if (submission.subjectId) {
        await replayOfflineSession(
          submission.subjectId,
          submission.answers,
          submission.id,
          submission.exam ?? null,
        );
      } else {
        // Neither a session to submit against nor a subject to create one for.
        // Nothing can ever send this; keeping it would block the queue forever.
        await pending.remove(submission.id);
        continue;
      }
      await pending.remove(submission.id);
      sent += 1;
    } catch (error) {
      const permanent = error instanceof ApiError && error.status >= 400 && error.status < 500;

      if (permanent) {
        await pending.remove(submission.id);
        continue;
      }
      kept += 1;
    }
  }

  // Tell open screens: the server has new answers, so readiness has moved.
  if (sent > 0 && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SYNCED_EVENT, { detail: { sent } }));
  }
  return { sent, kept };
}

/** Fired on `window` after queued answers reach the server. */
export const SYNCED_EVENT = "prepai:offline-synced";

/** Start draining on reconnect. Returns a teardown for tests and unmount. */
export function startOfflineSync(): () => void {
  const onOnline = () => void drainPending();

  window.addEventListener("online", onOnline);
  // Also once now: the tab may have been closed offline and reopened online,
  // in which case no `online` event ever fires.
  if (navigator.onLine) void drainPending();

  return () => window.removeEventListener("online", onOnline);
}
